import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Activity, Heart } from "lucide-react";

const HRVWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const tabs = t("tabs", { returnObjects: true }) as any[];
    const hrvTab = tabs.find((tab) => tab.id === "hrv");
    const widget = hrvTab?.widget || {};

    return (
        <motion.div
            className="relative w-full"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
        >
            {/* Main widget */}
            <div className="bg-gradient-to-br from-white via-white to-purple-50 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-purple-100 mb-3 hover:shadow-purple-100/30 transition-all duration-300">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center">
                        <motion.div
                            className={`${isRTL ? "ml-2" : "mr-2"} text-purple-600 bg-purple-100/70 p-1.5 rounded-full`}
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                        >
                            <Heart className="w-5 h-5" />
                        </motion.div>
                        <h5 className="text-purple-800 font-medium">{widget.title}</h5>
                    </div>
                    <motion.div
                        className="flex items-center bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 py-1.5 rounded-full shadow-sm"
                        whileHover={{ scale: 1.05, boxShadow: "0 3px 10px rgba(139, 92, 246, 0.3)" }}
                        transition={{ duration: 0.2 }}
                    >
                        <motion.span className="text-sm font-medium" animate={{ opacity: [1, 0.8, 1] }} transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}>
                            {widget.value}
                        </motion.span>
                        <span className="text-xs mr-1">{widget.unit}</span>
                    </motion.div>
                </div>

                {/* HRV chart */}
                <div className="relative h-32 mb-4 overflow-hidden rounded-lg bg-gradient-to-br from-purple-50 to-white group">
                    <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-purple-400/10 to-violet-500/10 opacity-30 group-hover:opacity-40"
                        animate={{ scale: [1, 1.02, 1] }}
                        transition={{ duration: 4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                    />

                    {/* HRV line chart */}
                    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 30" preserveAspectRatio="none">
                        <defs>
                            <linearGradient id="hrvLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.6" />
                                <stop offset="50%" stopColor="#8b5cf6" stopOpacity="1" />
                                <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.8" />
                            </linearGradient>
                            <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                                <feGaussianBlur stdDeviation="2" result="blur" />
                                <feComposite in="SourceGraphic" in2="blur" operator="over" />
                            </filter>
                        </defs>

                        <line x1="0" y1="15" x2="100" y2="15" stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="1,1" />

                        <motion.path
                            d="M 0,15 Q 5,10 10,18 T 20,12 T 30,20 T 40,8 T 50,15 T 60,10 T 70,22 T 80,15 T 90,5 T 100,15"
                            fill="none"
                            stroke="url(#hrvLineGradient)"
                            strokeWidth="1.8"
                            filter="url(#glow)"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 1 }}
                            transition={{ duration: 2.5, ease: "easeInOut" }}
                        />

                        <motion.path
                            d="M 0,15 Q 5,10 10,18 T 20,12 T 30,20 T 40,8 T 50,15 T 60,10 T 70,22 T 80,15 T 90,5 T 100,15 L 100,30 L 0,30 Z"
                            fill="url(#hrvLineGradient)"
                            fillOpacity="0.1"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 2, delay: 0.5, ease: "easeInOut" }}
                        />

                        <motion.circle
                            cx="50"
                            cy="15"
                            r="2.5"
                            fill="#8b5cf6"
                            filter="url(#glow)"
                            initial={{ opacity: 0 }}
                            animate={{
                                opacity: [0, 1, 0],
                                cx: [0, 20, 40, 60, 80, 100],
                                cy: [15, 12, 20, 8, 15, 15],
                                scale: [0.8, 1.2, 0.8],
                            }}
                            transition={{ duration: 5, repeat: Infinity, repeatType: "loop", ease: "easeInOut", times: [0, 0.2, 0.4, 0.6, 0.8, 1] }}
                        />
                    </svg>

                    <div className={`absolute bottom-2 ${isRTL ? "right-3" : "left-3"} text-xs text-purple-700 font-medium bg-white/70 px-2 py-0.5 rounded-md backdrop-blur-sm`}>
                        {widget.timeLabel}
                    </div>
                    <div className={`absolute top-2 ${isRTL ? "left-3" : "right-3"} text-xs text-purple-700 font-medium bg-white/70 px-2 py-0.5 rounded-md backdrop-blur-sm`}>{widget.unit}</div>

                    <motion.div
                        className={`absolute top-2 ${isRTL ? "right-3" : "left-3"} flex items-center bg-green-100 text-green-700 px-2 py-0.5 rounded-md text-xs font-medium`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1, duration: 0.5 }}
                    >
                        <motion.div
                            className={`w-1.5 h-1.5 rounded-full bg-green-500 ${isRTL ? "mr-1" : "ml-1"}`}
                            animate={{ scale: [1, 1.5, 1], opacity: [1, 0.7, 1] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        />
                        {widget.recording}
                    </motion.div>
                </div>

                {/* Nervous system status */}
                <div className="flex justify-between items-center mb-3 bg-purple-50/50 p-2 rounded-lg">
                    <div className="flex items-center">
                        <Activity className={`w-4 h-4 ${isRTL ? "ml-1.5" : "mr-1.5"} text-gray-600`} />
                        <span className="text-sm text-gray-600">{widget.nervousSystem?.label}</span>
                    </div>
                    <motion.div
                        className="flex items-center bg-green-100 text-green-700 px-2 py-1 rounded-lg"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                        whileHover={{ scale: 1.05 }}
                    >
                        <motion.div
                            className={`w-4 h-4 ${isRTL ? "mr-1" : "ml-1"} text-green-600`}
                            animate={{ scale: [1, 1.2, 1], rotate: [0, 10, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                            </svg>
                        </motion.div>
                        <span className="text-sm font-medium">{widget.nervousSystem?.value}</span>
                    </motion.div>
                </div>

                {/* Weekly trend */}
                <div className="mb-3">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-gray-600">{widget.weeklyTrend?.label}</span>
                        <span className="text-xs text-purple-600">{widget.weeklyTrend?.period}</span>
                    </div>
                    <div className="h-10 bg-purple-50/50 rounded-lg overflow-hidden relative flex items-end p-1">
                        {[62, 65, 58, 70, 68, 72, 68].map((value, index) => (
                            <motion.div
                                key={index}
                                className="flex-1 mx-0.5 rounded-t-sm bg-gradient-to-t from-purple-500 to-purple-400"
                                style={{ height: `${(value - 50) * 2}%` }}
                                initial={{ scaleY: 0 }}
                                animate={{ scaleY: 1 }}
                                transition={{ duration: 0.7, delay: index * 0.1, ease: "easeOut" }}
                                whileHover={{ scaleY: 1.1, backgroundColor: "#8b5cf6", transition: { duration: 0.2 } }}
                            />
                        ))}
                    </div>
                    <div className="flex justify-between mt-1 text-[10px] text-gray-500">
                        {widget.weekDays?.map((day: string, index: number) => (
                            <span key={index}>{day}</span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Sub-widgets */}
            <div className="grid grid-cols-2 gap-3 mb-3">
                {/* Stress level */}
                <motion.div
                    className="bg-gradient-to-br from-white to-purple-50/50 backdrop-blur-sm rounded-xl p-3 border border-purple-100 shadow-sm"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
                    whileHover={{ scale: 1.02, boxShadow: "0 4px 12px rgba(139, 92, 246, 0.15)" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#6b7280"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className={isRTL ? "mr-1" : "ml-1"}
                            >
                                <path d="M18 20V10" />
                                <path d="M12 20V4" />
                                <path d="M6 20v-6" />
                            </svg>
                            <span className="text-xs text-gray-600">{widget.stressLevel?.label}</span>
                        </div>
                        <motion.div className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-md flex items-center" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 ml-1"></span>
                            {widget.stressLevel?.value}
                        </motion.div>
                    </div>

                    <div className="relative h-14 flex items-center justify-center">
                        <svg width="65" height="65" viewBox="0 0 60 60">
                            <defs>
                                <linearGradient id="stressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#4ade80" />
                                    <stop offset="100%" stopColor="#22c55e" />
                                </linearGradient>
                            </defs>
                            <motion.circle cx="30" cy="30" r="25" fill="none" stroke="#e5e7eb" strokeWidth="6" strokeLinecap="round" />
                            <motion.circle
                                cx="30"
                                cy="30"
                                r="25"
                                fill="none"
                                stroke="url(#stressGradient)"
                                strokeWidth="6"
                                strokeLinecap="round"
                                strokeDasharray="157"
                                strokeDashoffset="117.75"
                                initial={{ strokeDashoffset: 157 }}
                                animate={{ strokeDashoffset: 117.75 }}
                                transition={{ duration: 1.5, delay: 0.5, ease: "easeOut" }}
                            />
                            <motion.text
                                x="32"
                                y="32"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="14"
                                fill="#374151"
                                fontWeight="bold"
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 1, duration: 0.5 }}
                            >
                                {widget.stressLevel?.percentage}
                            </motion.text>
                        </svg>
                    </div>
                </motion.div>

                {/* Nervous system balance */}
                <motion.div
                    className="bg-gradient-to-br from-white to-purple-50/50 backdrop-blur-sm rounded-xl p-3 border border-purple-100 shadow-sm"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.5, ease: "easeOut" }}
                    whileHover={{ scale: 1.02, boxShadow: "0 4px 12px rgba(139, 92, 246, 0.15)" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#6b7280"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className={isRTL ? "mr-1" : "ml-1"}
                            >
                                <path d="M12 2v20" />
                                <path d="M2 12h20" />
                            </svg>
                            <span className="text-xs text-gray-600 ms-0.5 mb-0.5">{widget.balance?.label}</span>
                        </div>
                    </div>

                    <div className="relative h-14 flex flex-col justify-center">
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2 overflow-hidden">
                            <motion.div
                                className="h-full rounded-full"
                                style={{ background: "linear-gradient(to right, #60a5fa, #8b5cf6)" }}
                                initial={{ width: 0 }}
                                animate={{ width: "60%" }}
                                transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
                            >
                                <motion.div
                                    className={`h-full w-1 bg-white absolute ${isRTL ? "left-0" : "right-0"} top-0`}
                                    animate={{ opacity: [0, 1, 0], height: ["0%", "100%", "0%"] }}
                                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                                />
                            </motion.div>
                        </div>
                        <div className="flex justify-between text-[10px] text-gray-600">
                            <div className="flex items-center">
                                <motion.div className="w-1.5 h-1.5 rounded-full bg-blue-500 ml-1" animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }} />
                                <span>{widget.balance?.sympathetic}</span>
                            </div>
                            <div className="flex items-center">
                                <motion.div className="w-1.5 h-1.5 rounded-full bg-purple-500 ml-1" animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 1.5 }} />
                                <span>{widget.balance?.parasympathetic}</span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default HRVWidget;
